import { useState, useEffect } from "react";
import { doc, onSnapshot, setDoc } from "firebase/firestore";
import { db } from "../firebase/config";

export interface TutorialStepConfig {
  element: string;
  title: string;
  description: string;
  side?: "top" | "bottom" | "left" | "right";
  align?: "start" | "center" | "end";
}

export interface TutorialConfig {
  enabled: boolean;
  nextBtnText: string;
  prevBtnText: string;
  doneBtnText: string;
  step0: TutorialStepConfig[];
  step1: TutorialStepConfig[];
  step2: TutorialStepConfig[];
  step3: TutorialStepConfig[];
}

export const DEFAULT_TUTORIAL_CONFIG: TutorialConfig = {
  enabled: true,
  nextBtnText: "Siguiente",
  prevBtnText: "Atrás",
  doneBtnText: "¡Entendido!",
  step0: [
    {
      element: "#tour-step-size",
      title: "🍕 Paso 1: Tamaño",
      description: "Empieza eligiendo el tamaño de la pizza que deseas para ti y tu familia.",
      side: "bottom",
      align: "center",
    },
    {
      element: ".progress-bar",
      title: "📍 Tu progreso",
      description: "Aquí arriba siempre podrás ver en qué paso estás y volver a uno anterior si cambias de opinión.",
      side: "bottom",
      align: "center",
    },
  ],
  step1: [
    {
      element: "#tour-step-slice",
      title: "✂️ Paso 2: División",
      description: "¿Entera o por mitades? Decide si quieres una pizza de un solo sabor o si prefieres combinar sabores.",
      side: "bottom",
      align: "center",
    },
  ],
  step2: [
    {
      element: "#tour-step-flavor",
      title: "🎨 Paso 3: Sabores",
      description: "¡Elige tus sabores favoritos! Si dividiste la pizza, podrás elegir un sabor para cada porción.",
      side: "bottom",
      align: "center",
    },
    {
      element: ".cart-footer-wrapper",
      title: "🛒 Agregar al carrito",
      description: "Cuando termines de armar esta pizza, toca aquí para agregarla a tu carrito.",
      side: "top",
      align: "center",
    },
  ],
  step3: [
    {
      element: "#tour-step-additions",
      title: "🥤 Adiciones",
      description: "Antes de confirmar, agrega bebidas, postres y llena los datos de tu entrega.",
      side: "bottom",
      align: "center",
    },
    {
      element: "#tour-step-summary",
      title: "✅ Confirmar Pedido",
      description: "Por último, revisa tu pedido, elige el método de pago y confírmalo. ¡Nosotros nos encargamos del resto!",
      side: "top",
      align: "center",
    },
  ],
};

export const useTutorialConfig = (restaurantId: string | undefined) => {
  const [tutorialConfig, setTutorialConfig] = useState<TutorialConfig>(DEFAULT_TUTORIAL_CONFIG);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!restaurantId) {
      setLoading(false);
      return;
    }

    const docRef = doc(db, "restaurants", restaurantId, "config", "tutorial");
    const unsubscribe = onSnapshot(docRef, (snap) => {
      if (snap.exists()) {
        const data = snap.data() as TutorialConfig;
        setTutorialConfig({
          enabled: data.enabled ?? DEFAULT_TUTORIAL_CONFIG.enabled,
          nextBtnText: data.nextBtnText || DEFAULT_TUTORIAL_CONFIG.nextBtnText,
          prevBtnText: data.prevBtnText || DEFAULT_TUTORIAL_CONFIG.prevBtnText,
          doneBtnText: data.doneBtnText || DEFAULT_TUTORIAL_CONFIG.doneBtnText,
          step0: data.step0 || DEFAULT_TUTORIAL_CONFIG.step0,
          step1: data.step1 || DEFAULT_TUTORIAL_CONFIG.step1,
          step2: data.step2 || DEFAULT_TUTORIAL_CONFIG.step2,
          step3: data.step3 || DEFAULT_TUTORIAL_CONFIG.step3,
        });
      } else {
        setTutorialConfig(DEFAULT_TUTORIAL_CONFIG);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [restaurantId]);

  const updateTutorialConfig = async (newConfig: Partial<TutorialConfig>) => {
    if (!restaurantId) return;
    const docRef = doc(db, "restaurants", restaurantId, "config", "tutorial");
    await setDoc(docRef, newConfig, { merge: true });
  };

  return { tutorialConfig, loading, updateTutorialConfig };
};
